import React, { useState } from 'react';
import { PrimeProcess } from '../../types/primeModel';
import { ProcessType, DemandFlowType } from '../../types/enums';

interface PrimeProcessFormProps {
  initialData?: Partial<PrimeProcess>;
  onSubmit: (process: PrimeProcess) => void;
  onCancel: () => void;
}

export const PrimeProcessForm: React.FC<PrimeProcessFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const [currentProcess, setCurrentProcess] = useState<Partial<PrimeProcess>>(
    initialData || {
      name: '',
      description: '',
      process_type: ProcessType.DEMAND,
      demand_flow_type: DemandFlowType.DRUM,
      process_parameters: {},
      prime_artifact_ref: '',
      demand_calculation_rules: {},
      validation_rules: {},
      improvement_ref: undefined,
      improvement_parameters: {},
      created_at: new Date().toISOString(),
      updated_at: undefined,
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidProcess(currentProcess)) {
      onSubmit(currentProcess as PrimeProcess);
    }
  };

  const isValidProcess = (process: Partial<PrimeProcess>): process is PrimeProcess => {
    return !!(
      process.name &&
      process.description &&
      process.process_type &&
      process.demand_flow_type &&
      process.prime_artifact_ref
    );
  };

  return (
    <form onSubmit={handleSubmit} className="prime-process-form">
      <h2>Process Definition</h2>

      <div className="form-group">
        <label htmlFor="name">Process Name</label>
        <input
          type="text"
          id="name"
          value={currentProcess.name}
          onChange={(e) => setCurrentProcess((prev) => ({ ...prev, name: e.target.value }))}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Process Description</label>
        <textarea
          id="description"
          value={currentProcess.description || ''}
          onChange={(e) => setCurrentProcess((prev) => ({ ...prev, description: e.target.value }))}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="processType">Process Type</label>
        <select
          id="processType"
          value={currentProcess.process_type}
          onChange={(e) =>
            setCurrentProcess((prev) => ({
              ...prev,
              process_type: e.target.value as PrimeProcess['process_type'],
            }))
          }
          required
        >
          <option value={ProcessType.DEFAULT}>Default (Unassigned)</option>
          <option value={ProcessType.DEMAND}>Demand</option>
          <option value={ProcessType.SUPPLY}>Supply</option>
          <option value={ProcessType.DELIVERY}>Delivery</option>
          <option value={ProcessType.FINANCE}>Finance</option>
          <option value={ProcessType.HR}>HR</option>
          <option value={ProcessType.IT}>IT</option>
          <option value={ProcessType.STRATEGY}>Strategy</option>
          <option value={ProcessType.IMPROVEMENT}>Improvement</option>
          <option value={ProcessType.QUALITY}>Quality</option>
          <option value={ProcessType.COMPLIANCE}>Compliance</option>
          <option value={ProcessType.RESEARCH}>Research</option>
          <option value={ProcessType.MARKETFIT}>MarketFit</option>
          <option value={ProcessType.FITFINDER}>FitFinder</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="demandFlowType">Demand Flow Type</label>
        <select
          id="demandFlowType"
          value={currentProcess.demand_flow_type}
          onChange={(e) =>
            setCurrentProcess((prev) => ({
              ...prev,
              demand_flow_type: e.target.value as PrimeProcess['demand_flow_type'],
            }))
          }
          required
        >
          <option value={DemandFlowType.DRUM}>Drum</option>
          <option value={DemandFlowType.PUSH}>Push</option>
          <option value={DemandFlowType.PULL}>Pull</option>
        </select>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-button">
          Cancel
        </button>
        <button type="submit" className="create-button" disabled={!isValidProcess(currentProcess)}>
          Save Process
        </button>
      </div>
    </form>
  );
};
